<template>
    <modal :open="showModal" @close="closeModal">
        <template v-if="showModal">
            <div class="bg-primary-500 modal-header">
                <button type="button" @click="closeModal()" class="close" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                </button>
                <div class="text-center text-white">
                    <h4 class="modal-title">{{ $t('organization_details.new_admin') }}</h4>
                    <small><b>{{ $t('organization_details.new_admin_modal_info') }}</b></small>
                </div>
            </div>
            <div class="flex flex-col items-center modal-body">
                <form class="lg:w-3/4 pb-6 w-full" @submit.prevent>
                    <div class="flex">
                        <div class="flex-grow input-with-icon mr-3">
                            <input class="form-control" id="search_query" name="search_query" type="text" :placeholder="$t('users.search_placeholder')" autocomplete="off" v-model="searchQuery" />
                            <span class="icon icon-search input-icon"></span>
                        </div>
                    </div>
                </form>

                <div v-if="displayedUsers.length === 0" class="flex flex-col items-center justify-center w-full">
                    <div class="md:w-1/4 pb-8 w-1/2">
                        <svg-file-searching></svg-file-searching>
                    </div>
                    <div class="md:w-1/2 text-center w-full">
                        <h5>{{ $t('globals.no_results_found') }}</h5>
                        <p>{{ $t('globals.no_results_found_tip') }}</p>
                    </div>
                </div>

                <div v-else class="w-full">
                    <div class="card input-border-color mb-3" v-for="user in displayedUsers">
                        <div class="card-body lg:p-4 p-3">
                            <div class="flex flex-row items-center">
                                <div class="lg:mr-4 mr-3">
                                    <avatar :user="user" :size="12"></avatar>
                                </div>
                                <div class="flex-1 lg:mr-4 mr-3">
                                    <h4 class="text-base mb-1 mt-0">{{ `${user.first_name} ${user.last_name}` }}</h4>
                                    <div>{{ user.email }}</div>
                                </div>
                                <div>
                                    <button v-if="user.id === organization.owner_id" class="btn btn-primary btn-sm" disabled="true">
                                        {{ $t('globals.owner') }}
                                    </button>
                                    <button v-else-if="user.is_admin" class="btn btn-primary btn-sm" disabled="true">
                                        {{ $t('globals.admin') }}
                                    </button>
                                    <button v-else @click="addAdmin(user.id)" class="btn btn-outline-primary btn-sm">
                                        {{ $t('globals.select') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <ul v-if="totalPages && totalPages > 1" class="flex justify-center pagination">
                        <li :class="{ 'disabled': currentPage === 1 }">
                            <a @click="currentPage !== 1 && currentPage--" href="javascript:;">&laquo;</a>
                        </li>
                        <li :class="{ 'active': page.active, 'disabled': page.disabled }" v-for="page in displayedPages">
                            <a v-if="page.disabled" href="javascript:;">...</a>
                            <a v-else @click="currentPage = page.label" href="javascript:;">{{ page.label }}</a>
                        </li>
                        <li :class="{ 'disabled': currentPage === totalPages }">
                            <a @click="currentPage !== totalPages && currentPage++" href="javascript:;">&raquo;</a>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="modal-footer"></div>
        </template>
    </modal>
</template>

<script>
import Modal from '../../partials/modal.vue';
import SvgFileSearching from '../../../svg/file-searching.vue';

export default {
    components: {
        'modal': Modal,
        'svg-file-searching': SvgFileSearching,
    },

    props: {
        organization: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            showModal: false,
            users: this.organization.users,
            searchQuery: '',
            currentPage: 1,
            perPage: 3,
            pages: [],
            form: new Form({
                user_id: '',
            }),
        };
    },

    computed: {
        displayedUsers() {
            let from = (this.currentPage * this.perPage) - this.perPage;
            let to = (this.currentPage * this.perPage);
            return this.users.slice(from, to);
        },

        totalPages() {
            return [...this.pages].pop();
        },

        displayedPages() {
            const previousPage = this.currentPage > 1 ? (this.currentPage - 1) : 1;
            const nextPage = this.currentPage < this.totalPages ? (this.currentPage + 1) : this.totalPages;
            const secondPreviousPage = previousPage === 3 ? 2 : null;
            const secondNextPage = nextPage === (this.totalPages - 2) ? (this.totalPages - 1) : null;
            const dotsBefore = previousPage > 3 ? 2 : null;
            const dotsAfter = nextPage < (this.totalPages - 2) ? (this.totalPages - 1) : null;

            let displayedPages = [];
            for (let page = 1; page <= this.totalPages; page++) {
                if ([1, this.totalPages, this.currentPage, previousPage, nextPage, secondPreviousPage, secondNextPage, dotsBefore, dotsAfter].includes(page)) {
                    displayedPages.push({
                        label: page,
                        active: this.currentPage === page,
                        disabled: [dotsBefore, dotsAfter].includes(page)
                    });
                }
            }
            return displayedPages;
        },
    },

    created() {
        Bus.$on('showAddAdminModal', () => {
            this.openModal();
        });
    },

    mounted() {
        this.setPages();
    },

    watch: {
        searchQuery(value) {
            this.users = this.organization.users.filter(user => {
                const fullName = (`${user.first_name} ${user.last_name}`)
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '');
                return fullName.includes(value.toLowerCase());
            });
            this.currentPage = 1;
            this.setPages();
        },
    },

    methods: {
        openModal() {
            this.searchQuery = '';
            this.currentPage = 1;
            this.showModal = true;
        },

        closeModal() {
            this.showModal = false;
            this.searchQuery = '';
            this.currentPage = 1;
        },

        setPages() {
            this.pages = [];
            const numberOfPages = Math.ceil(this.users.length / this.perPage);
            for (let index = 1; index <= numberOfPages; index++) {
                this.pages.push(index);
            }
        },

        addAdmin(userId) {
            this.form.user_id = userId;

            Globals.put('/api/v1/toggle-user-is-admin-flag', this.form)
                .then(() => window.location.reload())
                .catch(() => {
                    const message = this.form.errors.has('user_id') ? this.form.errors.get('user_id') : this.$t('globals.unexpected_error');
                    this.closeModal();
                    window.toastr.clear();
                    window.toastr.error(message);
                });
        },
    },
};
</script>
